import { useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { playtest, listHunts } from '../services/api';

export function AdminPage() {
  const location = useLocation();

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center gap-4 mb-8">
        <h1 className="text-2xl font-bold text-primary">Admin Console</h1>
        <span className="badge-preview">ADMIN</span>
      </div>

      <nav className="flex gap-4 mb-8 border-b border-gray-200 pb-4">
        <NavLink to="/admin" active={location.pathname === '/admin'}>
          Dashboard
        </NavLink>
        <NavLink to="/admin/hunts" active={location.pathname.startsWith('/admin/hunts')}>
          Hunts
        </NavLink>
        <NavLink to="/admin/playtest" active={location.pathname === '/admin/playtest'}>
          Playtest
        </NavLink>
      </nav>

      <Routes>
        <Route path="/" element={<AdminDashboard />} />
        <Route path="/hunts" element={<AdminHunts />} />
        <Route path="/playtest" element={<AdminPlaytest />} />
      </Routes>
    </div>
  );
}

function NavLink({ to, active, children }: { to: string; active: boolean; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'text-text-light hover:text-primary hover:bg-gray-100'
      }`}
    >
      {children}
    </Link>
  );
}

function AdminDashboard() {
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-primary mb-2">Hunt #1</h3>
        <p className="text-3xl font-bold text-text-dark">0</p>
        <p className="text-sm text-text-light">Completions</p>
      </div>
      <div className="card">
        <h3 className="text-lg font-semibold text-primary mb-2">Active Players</h3>
        <p className="text-3xl font-bold text-text-dark">0</p>
        <p className="text-sm text-text-light">Currently hunting</p>
      </div>
      <div className="card">
        <h3 className="text-lg font-semibold text-primary mb-2">Prize Status</h3>
        <p className="text-lg font-bold text-accent">$150 Unclaimed</p>
        <p className="text-sm text-text-light">First solver prize</p>
      </div>
    </div>
  );
}

function AdminHunts() {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-text-dark">Manage Hunts</h2>
        <button className="btn-primary">Create Hunt</button>
      </div>

      <div className="card">
        <p className="text-text-light">Hunt management interface coming soon.</p>
        <p className="text-sm text-text-light mt-2">
          Use the API to create/update hunts for now, or seed from the scripts.
        </p>
      </div>
    </div>
  );
}

function AdminPlaytest() {
  const navigate = useNavigate();
  const { data: huntsData } = useQuery({ queryKey: ['hunts'], queryFn: listHunts });
  const hunts = huntsData?.hunts ?? [];
  const [huntId, setHuntId] = useState('hunt-1');
  const [busy, setBusy] = useState<'start' | 'reset' | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const run = async (action: 'start' | 'reset') => {
    setBusy(action);
    setMessage(null);
    try {
      const result = await playtest(huntId, action);
      setMessage({ type: 'success', text: result.message });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-text-dark mb-6">Playtest Mode</h2>

      <div className="card">
        <h3 className="font-semibold text-text-dark mb-2">Preview Mode</h3>
        <p className="text-text-light text-sm mb-4">
          Test hunts without affecting real stats, leaderboards, or prize eligibility.
          All submissions in preview mode are flagged and excluded.
        </p>

        <label htmlFor="playtest-hunt" className="block text-sm font-medium text-text-dark mb-1">
          Hunt
        </label>
        {hunts.length > 0 ? (
          <select
            id="playtest-hunt"
            value={huntId}
            onChange={(e) => setHuntId(e.target.value)}
            className="input max-w-xs mb-4"
          >
            {hunts.map((h) => (
              <option key={h.huntId} value={h.huntId}>
                {h.title} ({h.huntId})
              </option>
            ))}
          </select>
        ) : (
          <input
            id="playtest-hunt"
            type="text"
            value={huntId}
            onChange={(e) => setHuntId(e.target.value)}
            className="input max-w-xs mb-4"
            placeholder="hunt-1"
          />
        )}

        <div className="flex flex-wrap gap-3">
          <button
            className="btn-primary disabled:opacity-50"
            onClick={() => run('start')}
            disabled={busy !== null}
          >
            {busy === 'start' ? 'Starting...' : 'Start / Restart at Clue 1'}
          </button>
          <button
            className="btn-secondary disabled:opacity-50"
            onClick={() => navigate(`/h/${huntId}`)}
            disabled={busy !== null}
          >
            Go Play →
          </button>
          <button
            className="px-4 py-2 rounded-lg font-medium text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
            onClick={() => run('reset')}
            disabled={busy !== null}
          >
            {busy === 'reset' ? 'Clearing...' : 'Clear Progress'}
          </button>
        </div>

        {message && (
          <div
            className={`mt-4 p-3 rounded-lg text-sm ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <p className="text-xs text-text-light mt-4">
          <strong>Start / Restart</strong> wipes your progress for this hunt and begins a fresh
          run at clue 1 flagged as preview (no prize or leaderboard impact). Use{' '}
          <strong>Go Play</strong> to jump into the hunt. <strong>Clear Progress</strong> removes
          your progress entirely.
        </p>
      </div>
    </div>
  );
}
